import axios from "axios";
import React from "react";
import { MdCheckCircle, MdDescription, MdErrorOutline, MdKeyboardArrowRight } from "react-icons/md";
import { getRequestHeaders } from "../api";
import { baseUrl } from "../assets/Animations";
import "./teacher-agreements.css";

const errorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.response?.data?.error || fallback;

const formatSignedAt = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ""
    : date.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
};

const TeacherAgreementCenter = ({ userData }) => {
  const [documents, setDocuments] = React.useState([]);
  const [signedHistory, setSignedHistory] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState("");
  const [openDocument, setOpenDocument] = React.useState(null);
  const [signatureName, setSignatureName] = React.useState("");
  const [initials, setInitials] = React.useState({});
  const [agreed, setAgreed] = React.useState(false);
  const [electronicConsent, setElectronicConsent] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [signError, setSignError] = React.useState("");

  const loadDocuments = React.useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const response = await axios.get(
        `${baseUrl}/teacher-agreements/me`,
        getRequestHeaders()
      );
      setDocuments(response.data?.documents || []);
      setSignedHistory(response.data?.signedHistory || []);
    } catch (error) {
      setLoadError(errorMessage(error, "Unable to load your agreements."));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const beginReview = (document) => {
    setOpenDocument(document);
    setSignatureName("");
    setInitials({});
    setAgreed(false);
    setElectronicConsent(false);
    setSignError("");
  };

  const closeDocument = () => {
    if (!submitting) setOpenDocument(null);
  };

  const signDocument = async () => {
    if (!openDocument) return;
    setSubmitting(true);
    setSignError("");
    try {
      const response = await axios.post(
        `${baseUrl}/teacher-agreements/documents/${openDocument.id}/sign`,
        {
          signatureName,
          agreed,
          electronicSignatureConsent: electronicConsent,
          initials: Object.entries(initials).map(([sectionId, value]) => ({
            sectionId,
            initials: value,
          })),
        },
        getRequestHeaders()
      );
      const signature = response.data.signature;
      setDocuments((current) =>
        current.map((document) =>
          document.id === openDocument.id ? { ...document, signature } : document
        )
      );
      setOpenDocument((current) => ({ ...current, signature }));
    } catch (error) {
      setSignError(errorMessage(error, "Unable to save your signature."));
    } finally {
      setSubmitting(false);
    }
  };

  const requiredInitialsComplete = openDocument
    ? openDocument.sections
        .filter((section) => section.requiresInitials)
        .every((section) => String(initials[section.sectionId] || "").trim())
    : false;
  const canSign =
    signatureName.trim().length > 1 &&
    agreed &&
    electronicConsent &&
    requiredInitialsComplete;
  const unsignedCount = documents.filter((document) => !document.signature).length;

  return (
    <>
      <section className="teacher-profile__card teacher-agreement-center">
        <div className="teacher-profile__card-header teacher-agreement-center__header">
          <div>
            <div className="teacher-profile__card-title">Documents &amp; Agreements</div>
            <p className="teacher-profile__card-subtitle">
              Review your school’s current contractor documents and keep signed copies here.
            </p>
          </div>
          {!loading && unsignedCount > 0 && (
            <span className="teacher-agreement-center__count">{unsignedCount} to sign</span>
          )}
        </div>

        {loading && <div className="teacher-agreement-center__loading">Loading documents…</div>}
        {loadError && (
          <div className="teacher-agreement-center__error">
            <MdErrorOutline />
            <span>{loadError}</span>
            <button type="button" onClick={loadDocuments}>Try again</button>
          </div>
        )}
        {!loading && !loadError && documents.length === 0 && (
          <div className="teacher-agreement-center__empty">
            You have no documents awaiting review.
          </div>
        )}
        <div className="teacher-agreement-center__list">
          {documents.map((document) => (
            <button type="button" key={document.id} onClick={() => beginReview(document)}>
              <span className="teacher-agreement-center__document-icon"><MdDescription /></span>
              <span className="teacher-agreement-center__document-copy">
                <strong>{document.title}</strong>
                <small>Version {document.version}</small>
              </span>
              {document.signature ? (
                <span className="teacher-agreement-center__signed"><MdCheckCircle /> Signed</span>
              ) : (
                <span className="teacher-agreement-center__review">Review &amp; sign</span>
              )}
              <MdKeyboardArrowRight />
            </button>
          ))}
        </div>
        {!loading && !loadError && signedHistory.length > 0 && (
          <div className="teacher-agreement-center__history">
            <div className="teacher-agreement-center__history-title">
              Previously signed copies
            </div>
            {signedHistory.map((document) => (
              <button
                type="button"
                key={document.signature?.id || `${document.id}-${document.version}`}
                onClick={() => beginReview(document)}
              >
                <span>
                  <strong>{document.title}</strong>
                  <small>
                    Version {document.version} · Signed {formatSignedAt(document.signature?.signedAt)}
                  </small>
                </span>
                <MdKeyboardArrowRight />
              </button>
            ))}
          </div>
        )}
      </section>

      {openDocument && (
        <div className="teacher-agreement-modal__cover" role="presentation">
          <div className="teacher-agreement-modal" role="dialog" aria-modal="true">
            <header className="teacher-agreement-modal__header">
              <div>
                <span>{openDocument.schoolName}</span>
                <h2>{openDocument.title}</h2>
                <p>Version {openDocument.version}</p>
              </div>
              <button type="button" onClick={closeDocument} aria-label="Close agreement">×</button>
            </header>

            <div className="teacher-agreement-modal__body">
              {openDocument.sections.map((section) => (
                <section key={section.sectionId} className="teacher-agreement-modal__section">
                  {section.title && <h3>{section.title}</h3>}
                  <p>{section.body}</p>
                  {section.requiresInitials && !openDocument.signature && (
                    <label className="teacher-agreement-modal__initials">
                      <span>Initials</span>
                      <input
                        value={initials[section.sectionId] || ""}
                        maxLength={20}
                        onChange={(event) => {
                          const value = event.currentTarget.value;
                          setInitials((current) => ({
                            ...current,
                            [section.sectionId]: value,
                          }));
                        }}
                      />
                    </label>
                  )}
                  {section.requiresInitials && openDocument.signature && (
                    <div className="teacher-agreement-modal__recorded-initials">
                      Initialed: {openDocument.signature.initials?.find(
                        (item) => item.sectionId === section.sectionId
                      )?.initials || "Recorded"}
                    </div>
                  )}
                </section>
              ))}

              <section className="teacher-agreement-modal__company-signature">
                <span>Authorized company signature</span>
                <strong>{openDocument.authorizedSigner?.signatureText}</strong>
                <p>
                  {openDocument.authorizedSigner?.name} · {openDocument.authorizedSigner?.title}
                </p>
              </section>

              {openDocument.signature ? (
                <section className="teacher-agreement-modal__signed-card">
                  <MdCheckCircle />
                  <div>
                    <strong>Signed by {openDocument.signature.signatureName}</strong>
                    <p>{formatSignedAt(openDocument.signature.signedAt)}</p>
                    <small>Document record: {openDocument.signature.documentHash?.slice(0, 12)}</small>
                  </div>
                </section>
              ) : (
                <section className="teacher-agreement-modal__signing">
                  <h3>Your signature</h3>
                  <label className="teacher-agreement-modal__signature-input">
                    <span>Type your full legal name</span>
                    <input
                      value={signatureName}
                      placeholder={userData?.Name || "Full name"}
                      onChange={(event) => setSignatureName(event.target.value)}
                    />
                  </label>
                  <label className="teacher-agreement-modal__checkbox">
                    <input
                      type="checkbox"
                      checked={agreed}
                      onChange={(event) => setAgreed(event.target.checked)}
                    />
                    <span>I have reviewed this document and agree to its terms.</span>
                  </label>
                  <label className="teacher-agreement-modal__checkbox">
                    <input
                      type="checkbox"
                      checked={electronicConsent}
                      onChange={(event) => setElectronicConsent(event.target.checked)}
                    />
                    <span>I consent to use electronic records and signatures.</span>
                  </label>
                  {signError && <div className="teacher-agreement-modal__error">{signError}</div>}
                </section>
              )}
            </div>

            <footer className="teacher-agreement-modal__footer">
              <button type="button" className="is-secondary" onClick={closeDocument}>
                {openDocument.signature ? "Close" : "Review Later"}
              </button>
              {!openDocument.signature && (
                <button
                  type="button"
                  className="is-primary"
                  disabled={!canSign || submitting}
                  onClick={signDocument}
                >
                  {submitting ? "Saving signature…" : "Sign Document"}
                </button>
              )}
            </footer>
          </div>
        </div>
      )}
    </>
  );
};

export default TeacherAgreementCenter;
